import { useKohdeSynkka } from "./Kohdekonteksti";
import { useCallback, useEffect, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { api, ApiError, KOHDE_TYOLAJI, type Kayttaja, type Kohde, type IvRakenne, type IvMittalaite, type IvTiiviysluokka,
         type IvLaskutusRivi, type IvOsuus, type IvPaate, type IvTulos } from "./api";
import { Tilasana, type Tilalaji } from "./Tila";
import { naytaPvm } from "./pvm";
import { t, esim, lokaali, virheTeksti, type KaannosAvain } from "./kieli";

/* ═══════════════════════════════════════════════════════════════
   IV-asennus (189, Toni 2.10.2026 «kaikki demon kautta»).

   kohde → järjestelmä (TK01) → kanavaosuus (tiiviysmittauksen yksikkö) →
   pääte (ilmavirtamittauksen yksikkö). Tulos lasketaan kannassa:
   raja = luokan kerroin × koepaine^0,65 × pinta-ala, ja mittalaitteen
   kalibroinnin voimassaolo jäädytetään mittausriville. Näkymä ei laske
   mitään itse — se näyttää kannan tuloksen ja syyn sanoin.

   Kolme tilaa, kolme muotoa (Tila.tsx): hyväksytty = muste, hylätty =
   vika, ei kelpaa (kalibrointi vanhentunut) = varoitus, mittaamaton =
   oma sana. Ei koskaan tyhjä.
   ═══════════════════════════════════════════════════════════════ */

const TULOS: Record<IvTulos, { sana: KaannosAvain; laji: Tilalaji }> = {
  hyvaksytty: { sana: "iv.tulos.hyvaksytty", laji: "kunnossa" },
  hylatty:    { sana: "iv.tulos.hylatty",    laji: "vika" },
  ei_kelpaa:  { sana: "iv.tulos.ei_kelpaa",  laji: "varoitus" },
};
function Tulos({ tulos, syy }: { tulos: IvTulos | null; syy?: string | null }) {
  if (!tulos) return <Tilasana laji="ei_mitattu">{t("iv.mittaamatta")}</Tilasana>;
  return <Tilasana laji={TULOS[tulos].laji} title={syy ?? undefined}>{t(TULOS[tulos].sana)}</Tilasana>;
}
function pros(v: number | null | undefined): string { return v == null ? "—" : `${v.toLocaleString(lokaali(), { maximumFractionDigits: 1 })} %`; }
function luku(v: number | null | undefined, yks = ""): string { return v == null ? "—" : `${v.toLocaleString(lokaali(), { maximumFractionDigits: 3 })}${yks}`; }

export default function IvAsennus({ kayttaja }: { kayttaja: Kayttaja }) {
  const saaRakentaa = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  const saaMitata = saaRakentaa || kayttaja.roolit.includes("kenttatekija");
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohdeId, setKohdeId] = useState<number | "">("");
  // T426: yläpalkin kohde ohjaa. «Kaikki kohteet» ei kelpaa tälle näkymälle, joten silloin näkymä pitää oman oletuksensa
  // ja valitsimensa eikä työnnä arvaustaan yläpalkkiin.
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohdeId, setKohdeId, { kaikkiSallittu: false });
  const [rakenne, setRakenne] = useState<IvRakenne | null>(null);
  const [laitteet, setLaitteet] = useState<IvMittalaite[]>([]);
  const [luokat, setLuokat] = useState<IvTiiviysluokka[]>([]);
  const [laskutus, setLaskutus] = useState<IvLaskutusRivi[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [auki, setAuki] = useState<{ laji: "tiiviys" | "paatteet" | "ilmavirta" | "osuus" | "paate"; id: number } | null>(null);
  const [tyo, setTyo] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const k = await api.kohteet();
        setKohteet(k);
        // Oletus: ensimmäinen IV-työlajin kohde; ilman sellaista ensimmäinen kohde jolla on IV-rakenne ei ole tiedossa → ensimmäinen.
        const iv = k.find((x) => x.tyolaji === "iv_asennus") ?? k[0];
        if (iv) setKohdeId((v) => (v === "" ? iv.id : v));   // T426: yläpalkin kohde ei korvaudu oletuksella
        const [l, t] = await Promise.all([api.ivMittalaitteet(), api.ivTiiviysluokat()]);
        setLaitteet(l); setLuokat(t);
      } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
    })();
  }, []);

  const uusi = useViimeisin();   // päätös 99: kuittausnapit osuvat näytettyyn rakenteeseen — vain viimeisin kohde
  const lataa = useCallback(async () => {
    if (kohdeId === "") return;
    const tuore = uusi();
    try {
      const [r, l] = await Promise.all([api.ivRakenne(kohdeId), api.ivLaskutus(kohdeId)]);
      if (!tuore()) return;
      setRakenne(r); setLaskutus(l); setVirhe(null);
    } catch (e) { if (!tuore()) return; setRakenne(null); setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
  }, [kohdeId, uusi]);
  useEffect(() => { void lataa(); }, [lataa]);

  async function aja(f: () => Promise<unknown>, onnistui: string) {
    if (tyo) return;
    setTyo(true); setIlmoitus(null); setVirhe(null);
    try { await f(); setIlmoitus(onnistui); setAuki(null); await lataa(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tk.tallennus_virhe")); }
    finally { setTyo(false); }
  }

  const kohde = kohteet.find((k) => k.id === kohdeId);
  const avoimet = rakenne?.puutteet ?? [];

  return (
    <section className="panel iv" data-nakyma="iv_asennus">
      <div className="panel__head"><h2>{t("valikko.iv_asennus")}</h2></div>
      <p className="vihje">{t("iv.selite")}</p>
      <div className="rivi">
        {!(ylapalkissa && ylaValittu != null) && <label>{t("pu.kohde")}
          <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value === "" ? "" : Number(e.target.value))} data-iv-kohde>
            <option value="">{t("hl.valitse_viivat")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} {k.nimi}{k.tyolaji && k.tyolaji !== "iv_asennus" ? ` (${KOHDE_TYOLAJI[k.tyolaji]})` : ""}</option>)}
          </select>
        </label>}
        {kohde && kohde.tyolaji !== "iv_asennus" && (
          <Tilasana laji="varoitus">{t("iv.vaara_tyolaji", { laji: KOHDE_TYOLAJI[kohde.tyolaji ?? "muu"] })}</Tilasana>
        )}
      </div>
      {virhe && <p className="virhe" data-iv-virhe>{virhe}</p>}
      {ilmoitus && <p className="ilmoitus" data-iv-ilmoitus>{ilmoitus}</p>}

      {rakenne && (
        <>
          <div className="iv__yhteenveto" data-iv-valmius={rakenne.valmius ?? ""} data-iv-puutteita={avoimet.length}>
            <b>{t("iv.valmius", { kohde: rakenne.kohde.tunnus })}</b>{" "}
            {rakenne.valmius == null ? <Tilasana laji="ei_mitattu">{t("iv.ei_osuuksia")}</Tilasana>
              : <span data-luku="iv_valmius">{pros(rakenne.valmius)}</span>}
            {" · "}{t("iv.lukumaarat", { j: rakenne.jarjestelmat.length, o: rakenne.osuudet.length, p: rakenne.paatteet.length })}
            {" · "}{t("iv.avoimia_puutteita")} {avoimet.length === 0 ? <Tilasana laji="kunnossa">0</Tilasana> : <Tilasana laji="vika">{avoimet.length}</Tilasana>}
          </div>

          {avoimet.length > 0 && (
            <details open className="iv__puutteet" data-iv-puutelista>
              <summary>{t("iv.avoimet_puutteet", { n: avoimet.length })}</summary>
              <ul>
                {avoimet.map((p) => (
                  <li key={p.id} data-iv-puute={p.tunniste.replace(/_\d+$/, "")}>
                    <Tilasana laji={p.vaikutus === "pysayttaa" ? "vika" : "varoitus"}>{p.vaikutus === "pysayttaa" ? t("pu.luku.pysayttaa") : t("iv.hidastaa")}</Tilasana>{" "}
                    {p.kuvaus} <span className="vihje">({t("iv.havaittu", { pvm: naytaPvm(p.havaittu) ?? "" })})</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {rakenne.jarjestelmat.map((j) => {
            const osuudet = rakenne.osuudet.filter((o) => o.jarjestelma_id === j.id);
            return (
              <div key={j.id} className="iv__jarjestelma" data-iv-jarjestelma={j.tunnus} data-iv-jarjestelma-valmius={j.valmius ?? ""}>
                <h3>{j.tunnus} {j.nimi ?? ""} <span className="vihje">{j.palvelualue ?? ""}</span>
                  {" — "}{t("iv.valmius_pieni")} {j.valmius == null ? <Tilasana laji="ei_mitattu">{t("iv.ei_mitattavaa")}</Tilasana> : pros(j.valmius)}</h3>
                <table className="iv__osuudet">
                  <thead><tr>
                    <th>{t("iv.osuus")}</th><th>{t("iv.kuvaus")}</th><th className="td--oikea">m²</th><th>{t("iv.luokka")}</th><th>{t("iv.asennettu")}</th><th>{t("iv.p1")}</th>
                    <th>{t("iv.tiiviys")}</th><th className="td--oikea">{t("iv.paatteet_ok")}</th><th className="td--oikea">{t("iv.valmius_iso")}</th><th></th>
                  </tr></thead>
                  <tbody>
                    {osuudet.map((o) => (
                      <Osuus key={o.id} o={o} paatteet={rakenne.paatteet.filter((p) => p.kanavaosuus_id === o.id)} laitteet={laitteet}
                             saaMitata={saaMitata} saaRakentaa={saaRakentaa} tyo={tyo} auki={auki} setAuki={setAuki} aja={aja} />
                    ))}
                    {osuudet.length === 0 && <tr><td colSpan={10} className="vihje">{t("iv.ei_kanavaosuuksia")}</td></tr>}
                  </tbody>
                </table>
                {saaRakentaa && (auki?.laji === "osuus" && auki.id === j.id
                  ? <OsuusLomake jarjestelmaId={j.id} luokat={luokat} tyo={tyo} peru={() => setAuki(null)}
                                 tallenna={(s) => aja(() => api.luoKanavaosuus(s), t("iv.osuus_lisatty", { tunnus: s.tunnus }))} />
                  : <button className="nappi nappi--pieni" onClick={() => setAuki({ laji: "osuus", id: j.id })} disabled={tyo}>{t("iv.lisaa_osuus_nappi")}</button>)}
              </div>
            );
          })}
          {rakenne.jarjestelmat.length === 0 && <p className="vihje">{t("iv.ei_jarjestelmia")}</p>}

          {saaRakentaa && (
            <JarjestelmaLomake kohdeId={rakenne.kohde.id} tyo={tyo}
              tallenna={(s) => aja(() => api.luoIvJarjestelma(s), t("iv.jarjestelma_lisatty", { tunnus: s.tunnus }))} />
          )}

          {/* Päätös 46 (Toni 2.10.2026): laskutusyksikkö on alustavasti PÄÄTE. Vain lukeva rivi — ei hinnastossa. */}
          <details className="iv__laskutus" data-iv-laskutus>
            <summary>{t("iv.laskutus_otsikko")}</summary>
            <p className="vihje">{t("iv.laskutus_selite")}</p>
            <table><thead><tr><th>{t("iv.kuukausi")}</th><th className="td--oikea">{t("iv.paatteita_kirjattu")}</th><th className="td--oikea">{t("iv.ilmavirtamittauksia")}</th><th className="td--oikea">{t("iv.joista_hyvaksytty")}</th></tr></thead>
              <tbody>
                {laskutus.map((r) => <tr key={r.kk} data-iv-laskutus-kk={r.kk}><td>{r.kk}</td><td className="td--oikea">{r.paatteita_kirjattu}</td><td className="td--oikea">{r.ilmavirtamittauksia}</td><td className="td--oikea">{r.ilmavirta_hyvaksytty}</td></tr>)}
                {laskutus.length === 0 && <tr><td colSpan={4} className="vihje">{t("iv.ei_paatteita_mittauksia")}</td></tr>}
              </tbody></table>
          </details>

          <details className="iv__luokat">
            <summary>{t("iv.luokat_otsikko")}</summary>
            <table><thead><tr><th>{t("iv.luokka")}</th><th className="td--oikea">{t("iv.kerroin")}</th><th className="td--oikea">{t("iv.eksponentti")}</th><th>{t("iv.yksikko")}</th><th>{t("iv.lahde")}</th><th>{t("iv.huomio")}</th></tr></thead>
              <tbody>{luokat.map((l) => <tr key={l.luokka}><td>{l.luokka}</td><td className="td--oikea">{l.kerroin}</td><td className="td--oikea">{l.eksponentti}</td><td>{l.yksikko}</td><td>{l.lahde}</td><td>{l.huomio ?? ""}</td></tr>)}</tbody></table>
          </details>
        </>
      )}
      {!rakenne && kohdeId !== "" && !virhe && <p>{t("yleinen.ladataan")}</p>}
    </section>
  );
}

function Osuus({ o, paatteet, laitteet, saaMitata, saaRakentaa, tyo, auki, setAuki, aja }: {
  o: IvOsuus; paatteet: IvPaate[]; laitteet: IvMittalaite[]; saaMitata: boolean; saaRakentaa: boolean; tyo: boolean;
  auki: { laji: string; id: number } | null; setAuki: (a: { laji: "tiiviys" | "paatteet" | "ilmavirta" | "osuus" | "paate"; id: number } | null) => void;
  aja: (f: () => Promise<unknown>, onnistui: string) => Promise<void>;
}) {
  const on = (laji: string) => auki?.laji === laji && auki.id === o.id;
  return (
    <>
      <tr data-iv-osuus={o.tunnus} data-iv-osuus-valmius={o.valmius ?? ""} data-iv-tiiviys={o.tiiviys_tulos ?? "mittaamatta"}>
        <td><b>{o.tunnus}</b></td>
        <td>{o.kuvaus ?? ""}</td>
        <td className="td--oikea">{luku(o.pinta_ala_m2)}</td>
        <td>{o.vaadittu_luokka ?? <Tilasana laji="ei_mitattu">{t("iv.ei_annettu")}</Tilasana>}</td>
        <td>{o.asennettu_pvm ? naytaPvm(o.asennettu_pvm) : <Tilasana laji="ei_mitattu">{t("iv.ei")}</Tilasana>}
          {saaMitata && <button className="nappi nappi--pieni" disabled={tyo} onClick={() => aja(() => api.kuittaaKanavaosuus(o.id, "asennettu", !!o.asennettu_pvm), o.asennettu_pvm ? t("iv.asennus_peruttu") : t("iv.asennettu_kuitattu"))}>{o.asennettu_pvm ? t("iv.peru") : t("iv.kuittaa")}</button>}</td>
        <td>{o.p1_suojaus_pvm ? naytaPvm(o.p1_suojaus_pvm) : <Tilasana laji="ei_mitattu">{t("iv.ei")}</Tilasana>}
          {saaMitata && <button className="nappi nappi--pieni" disabled={tyo} onClick={() => aja(() => api.kuittaaKanavaosuus(o.id, "p1", !!o.p1_suojaus_pvm), o.p1_suojaus_pvm ? t("iv.p1_peruttu") : t("iv.p1_kuitattu"))}>{o.p1_suojaus_pvm ? t("iv.peru") : t("iv.kuittaa")}</button>}</td>
        <td><Tulos tulos={o.tiiviys_tulos} syy={o.tiiviys_syy} />
          {o.tiiviys_tulos && <span className="vihje"> {luku(o.vuotoilmavirta_ls)} / {t("iv.raja")} {luku(o.raja_ls)} dm³/s · {naytaPvm(o.tiiviys_paiva)}</span>}
          {o.tiiviys_syy && o.tiiviys_tulos !== "hyvaksytty" && <div className="vihje">{o.tiiviys_syy}</div>}</td>
        <td className="td--oikea">{o.paatteita === 0 ? <Tilasana laji="ei_mitattu">{t("iv.ei_paatteita")}</Tilasana> : `${o.paatteita_ok} / ${o.paatteita}`}</td>
        <td className="td--oikea">{o.valmius == null ? "—" : pros(o.valmius)}</td>
        <td>
          {saaMitata && <button className="nappi nappi--pieni" disabled={tyo} onClick={() => setAuki(on("tiiviys") ? null : { laji: "tiiviys", id: o.id })}>{t("iv.tiiviysmittaus_nappi")}</button>}{" "}
          <button className="nappi nappi--pieni" disabled={tyo} onClick={() => setAuki(on("paatteet") ? null : { laji: "paatteet", id: o.id })}>{t("iv.paatteet_n", { n: o.paatteita })}</button>
        </td>
      </tr>
      {on("tiiviys") && (
        <tr><td colSpan={10}>
          <TiiviysLomake osuus={o} laitteet={laitteet} tyo={tyo} peru={() => setAuki(null)}
            tallenna={(s) => aja(async () => { const r = await api.kirjaaTiiviysmittaus(s); return r; }, t("iv.tiiviys_kirjattu"))} />
        </td></tr>
      )}
      {on("paatteet") && (
        <tr><td colSpan={10}>
          <table className="iv__paatteet" data-iv-paatteet={o.tunnus}>
            <thead><tr><th>{t("iv.paate")}</th><th>{t("pu.tyyppi")}</th><th className="td--oikea">{t("iv.suunniteltu_ls")}</th><th className="td--oikea">{t("iv.mitattu_ls")}</th><th className="td--oikea">{t("iv.poikkeama")}</th><th>{t("iv.tulos")}</th><th>{t("iv.saato")}</th><th></th></tr></thead>
            <tbody>
              {paatteet.map((p) => (
                <PaateRivi key={p.id} p={p} laitteet={laitteet} saaMitata={saaMitata} tyo={tyo}
                  auki={auki?.laji === "ilmavirta" && auki.id === p.id}
                  avaa={() => setAuki(auki?.laji === "ilmavirta" && auki.id === p.id ? null : { laji: "ilmavirta", id: p.id })} aja={aja} />
              ))}
              {paatteet.length === 0 && <tr><td colSpan={8} className="vihje">{t("iv.ei_paatteita_selite")}</td></tr>}
            </tbody>
          </table>
          {saaRakentaa && <PaateLomake osuusId={o.id} tyo={tyo} tallenna={(s) => aja(() => api.luoPaate(s), t("iv.paate_lisatty", { tunnus: s.tunnus }))} />}
        </td></tr>
      )}
    </>
  );
}

function PaateRivi({ p, laitteet, saaMitata, tyo, auki, avaa, aja }: {
  p: IvPaate; laitteet: IvMittalaite[]; saaMitata: boolean; tyo: boolean; auki: boolean; avaa: () => void;
  aja: (f: () => Promise<unknown>, onnistui: string) => Promise<void>;
}) {
  return (
    <>
      <tr data-iv-paate={p.tunnus} data-iv-ilmavirta={p.tulos ?? "mittaamatta"}>
        <td>{p.tunnus}</td><td>{p.tyyppi ?? ""}</td>
        <td className="td--oikea">{luku(p.suunniteltu_ls)} <span className="vihje">±{p.toleranssi_pros} %</span></td>
        <td className="td--oikea">{luku(p.mitattu_ls)}</td>
        <td className="td--oikea">{pros(p.poikkeama_pros)}</td>
        <td><Tulos tulos={p.tulos} syy={p.tulos_syy} />{p.tulos_syy && p.tulos !== "hyvaksytty" && <div className="vihje">{p.tulos_syy}</div>}</td>
        <td>{p.saatoasento ?? ""}</td>
        <td>{saaMitata && <button className="nappi nappi--pieni" disabled={tyo} onClick={avaa}>{t("iv.ilmavirta_nappi")}</button>}</td>
      </tr>
      {auki && (
        <tr><td colSpan={8}>
          <IlmavirtaLomake paate={p} laitteet={laitteet} tyo={tyo} peru={avaa}
            tallenna={(s) => aja(() => api.kirjaaIlmavirtamittaus(s), t("iv.ilmavirta_kirjattu"))} />
        </td></tr>
      )}
    </>
  );
}

function MittalaiteValinta({ laitteet, arvo, aseta }: { laitteet: IvMittalaite[]; arvo: number | ""; aseta: (v: number | "") => void }) {
  const valittu = laitteet.find((l) => l.id === arvo);
  return (
    <label>{t("iv.mittalaite")}
      <select value={arvo} onChange={(e) => aseta(e.target.value === "" ? "" : Number(e.target.value))} data-iv-mittalaite>
        <option value="">{t("hl.valitse_viivat")}</option>
        {laitteet.map((l) => <option key={l.id} value={l.id}>{l.tunnus} {l.nimi}{l.kalibrointi_voimassa ? "" : ` — ${t("iv.kalibrointi_ei_voimassa_iso")}`}</option>)}
      </select>
      {valittu && (valittu.kalibrointi_voimassa
        ? <span className="vihje"> {t("iv.kalibrointi_voimassa", { pvm: naytaPvm(valittu.kalibrointi_voimassa_asti) ?? "" })}</span>
        : <Tilasana laji="varoitus"> {valittu.kalibrointi_voimassa_asti ? t("iv.kalibrointi_vanhentunut", { pvm: naytaPvm(valittu.kalibrointi_voimassa_asti) ?? "" }) : t("iv.kalibrointi_puuttuu")}</Tilasana>)}
      {laitteet.length === 0 && <Tilasana laji="varoitus"> {t("iv.ei_mittalaitteita")}</Tilasana>}
    </label>
  );
}

function TiiviysLomake({ osuus, laitteet, tyo, peru, tallenna }: {
  osuus: IvOsuus; laitteet: IvMittalaite[]; tyo: boolean; peru: () => void;
  tallenna: (s: { kanavaosuus_id: number; paiva?: string; koepaine_pa: number; vuotoilmavirta_ls: number; mittalaite_id: number; tarkastaja?: string | null; raportti?: string | null }) => void;
}) {
  const [paiva, setPaiva] = useState(new Date().toISOString().slice(0, 10));
  const [paine, setPaine] = useState("400");
  const [vuoto, setVuoto] = useState("");
  const [laite, setLaite] = useState<number | "">(laitteet.find((l) => l.kalibrointi_voimassa)?.id ?? "");
  const [tarkastaja, setTarkastaja] = useState("");
  const [raportti, setRaportti] = useState("");
  const ok = vuoto !== "" && paine !== "" && laite !== "";
  return (
    <form className="iv__lomake" data-iv-tiiviyslomake={osuus.tunnus} onSubmit={(e) => { e.preventDefault(); if (ok) tallenna({ kanavaosuus_id: osuus.id, paiva, koepaine_pa: Number(paine), vuotoilmavirta_ls: Number(vuoto.replace(",", ".")), mittalaite_id: laite as number, tarkastaja: tarkastaja || null, raportti: raportti || null }); }}>
      <b>{t("iv.tiiviysmittaus")} {osuus.tunnus}</b> <span className="vihje">{t("iv.pinta_ala_luokka", { ala: luku(osuus.pinta_ala_m2), luokka: osuus.vaadittu_luokka ?? t("iv.ei_annettu") })}</span>
      <div className="rivi">
        <label>{t("hl.paiva")} <input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} /></label>
        <label>{t("iv.koepaine")} <input type="number" min={1} step={1} value={paine} onChange={(e) => setPaine(e.target.value)} /></label>
        <label>{t("iv.vuotoilmavirta")} <input type="text" inputMode="decimal" value={vuoto} onChange={(e) => setVuoto(e.target.value)} data-iv-vuoto /></label>
        <MittalaiteValinta laitteet={laitteet} arvo={laite} aseta={setLaite} />
        <label>{t("iv.tarkastaja")} <input value={tarkastaja} onChange={(e) => setTarkastaja(e.target.value)} /></label>
        <label>{t("iv.poytakirja")} <input value={raportti} onChange={(e) => setRaportti(e.target.value)} /></label>
      </div>
      <button className="nappi" type="submit" disabled={!ok || tyo} data-iv-tiiviys-tallenna>{t("iv.kirjaa_mittaus")}</button>{" "}
      <button className="nappi nappi--pieni" type="button" onClick={peru}>{t("iv.peru_iso")}</button>
    </form>
  );
}

function IlmavirtaLomake({ paate, laitteet, tyo, peru, tallenna }: {
  paate: IvPaate; laitteet: IvMittalaite[]; tyo: boolean; peru: () => void;
  tallenna: (s: { paate_id: number; paiva?: string; mitattu_ls: number; saatoasento?: string | null; mittalaite_id: number; tarkastaja?: string | null }) => void;
}) {
  const [paiva, setPaiva] = useState(new Date().toISOString().slice(0, 10));
  const [mitattu, setMitattu] = useState("");
  const [saato, setSaato] = useState("");
  const [laite, setLaite] = useState<number | "">(laitteet.find((l) => l.kalibrointi_voimassa)?.id ?? "");
  const [tarkastaja, setTarkastaja] = useState("");
  const ok = mitattu !== "" && laite !== "";
  return (
    <form className="iv__lomake" data-iv-ilmavirtalomake={paate.tunnus} onSubmit={(e) => { e.preventDefault(); if (ok) tallenna({ paate_id: paate.id, paiva, mitattu_ls: Number(mitattu.replace(",", ".")), saatoasento: saato || null, mittalaite_id: laite as number, tarkastaja: tarkastaja || null }); }}>
      <b>{t("iv.ilmavirtamittaus")} {paate.tunnus}</b> <span className="vihje">{t("iv.suunniteltu_selite", { ls: luku(paate.suunniteltu_ls), tol: paate.toleranssi_pros ?? "" })}</span>
      <div className="rivi">
        <label>{t("hl.paiva")} <input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} /></label>
        <label>{t("iv.mitattu")} <input type="text" inputMode="decimal" value={mitattu} onChange={(e) => setMitattu(e.target.value)} data-iv-mitattu /></label>
        <label>{t("iv.saatoasento")} <input value={saato} onChange={(e) => setSaato(e.target.value)} /></label>
        <MittalaiteValinta laitteet={laitteet} arvo={laite} aseta={setLaite} />
        <label>{t("iv.tarkastaja")} <input value={tarkastaja} onChange={(e) => setTarkastaja(e.target.value)} /></label>
      </div>
      <button className="nappi" type="submit" disabled={!ok || tyo} data-iv-ilmavirta-tallenna>{t("iv.kirjaa_mittaus")}</button>{" "}
      <button className="nappi nappi--pieni" type="button" onClick={peru}>{t("iv.peru_iso")}</button>
    </form>
  );
}

function JarjestelmaLomake({ kohdeId, tyo, tallenna }: { kohdeId: number; tyo: boolean; tallenna: (s: { kohde_id: number; tunnus: string; nimi?: string | null; palvelualue?: string | null }) => void }) {
  const [tunnus, setTunnus] = useState(""); const [nimi, setNimi] = useState(""); const [alue, setAlue] = useState("");
  return (
    <form className="iv__lomake" data-iv-jarjestelmalomake onSubmit={(e) => { e.preventDefault(); if (tunnus.trim()) { tallenna({ kohde_id: kohdeId, tunnus: tunnus.trim(), nimi: nimi || null, palvelualue: alue || null }); setTunnus(""); setNimi(""); setAlue(""); } }}>
      <b>{t("iv.uusi_jarjestelma")}</b>
      <div className="rivi">
        <label>{t("iv.tunnus")} <input value={tunnus} onChange={(e) => setTunnus(e.target.value)} placeholder={esim("TK03")} /></label>
        <label>{t("iv.nimi")} <input value={nimi} onChange={(e) => setNimi(e.target.value)} placeholder={esim(t("iv.nimi_esim"))} /></label>
        <label>{t("iv.palvelualue")} <input value={alue} onChange={(e) => setAlue(e.target.value)} placeholder={esim(t("iv.palvelualue_esim"))} /></label>
        <button className="nappi nappi--paa" type="submit" disabled={!tunnus.trim() || tyo}>{t("iv.lisaa_jarjestelma")}</button>
        {!tunnus.trim() && <span className="este-syy" data-este-syy="">{t("iv.syy_tunnus_puuttuu")}</span>}
      </div>
    </form>
  );
}

function OsuusLomake({ jarjestelmaId, luokat, tyo, peru, tallenna }: { jarjestelmaId: number; luokat: IvTiiviysluokka[]; tyo: boolean; peru: () => void; tallenna: (s: { jarjestelma_id: number; tunnus: string; kuvaus?: string | null; pinta_ala_m2?: number | null; pituus_m?: number | null; vaadittu_luokka?: string | null }) => void }) {
  const [tunnus, setTunnus] = useState(""); const [kuvaus, setKuvaus] = useState(""); const [ala, setAla] = useState(""); const [pituus, setPituus] = useState(""); const [luokka, setLuokka] = useState("C");
  return (
    <form className="iv__lomake" onSubmit={(e) => { e.preventDefault(); if (tunnus.trim()) tallenna({ jarjestelma_id: jarjestelmaId, tunnus: tunnus.trim(), kuvaus: kuvaus || null, pinta_ala_m2: ala ? Number(ala.replace(",", ".")) : null, pituus_m: pituus ? Number(pituus.replace(",", ".")) : null, vaadittu_luokka: luokka || null }); }}>
      <b>{t("iv.uusi_osuus")}</b>
      <div className="rivi">
        <label>{t("iv.tunnus")} <input value={tunnus} onChange={(e) => setTunnus(e.target.value)} placeholder="K-13" /></label>
        <label>{t("iv.kuvaus")} <input value={kuvaus} onChange={(e) => setKuvaus(e.target.value)} /></label>
        <label>{t("iv.pinta_ala")} <input inputMode="decimal" value={ala} onChange={(e) => setAla(e.target.value)} /></label>
        <label>{t("iv.pituus")} <input inputMode="decimal" value={pituus} onChange={(e) => setPituus(e.target.value)} /></label>
        <label>{t("iv.vaadittu_luokka")} <select value={luokka} onChange={(e) => setLuokka(e.target.value)}><option value="">{t("iv.ei_annettu_viivat")}</option>{luokat.map((l) => <option key={l.luokka} value={l.luokka}>{l.luokka}</option>)}</select></label>
        <button className="nappi" type="submit" disabled={!tunnus.trim() || tyo}>{t("iv.lisaa_osuus")}</button>
        <button className="nappi nappi--pieni" type="button" onClick={peru}>{t("iv.peru_iso")}</button>
      </div>
    </form>
  );
}

function PaateLomake({ osuusId, tyo, tallenna }: { osuusId: number; tyo: boolean; tallenna: (s: { kanavaosuus_id: number; tunnus: string; tyyppi?: string | null; suunniteltu_ls?: number | null; toleranssi_pros?: number | null }) => void }) {
  const [tunnus, setTunnus] = useState(""); const [tyyppi, setTyyppi] = useState(""); const [ls, setLs] = useState(""); const [tol, setTol] = useState("10");
  return (
    <form className="iv__lomake" onSubmit={(e) => { e.preventDefault(); if (tunnus.trim()) { tallenna({ kanavaosuus_id: osuusId, tunnus: tunnus.trim(), tyyppi: tyyppi || null, suunniteltu_ls: ls ? Number(ls.replace(",", ".")) : null, toleranssi_pros: tol ? Number(tol) : null }); setTunnus(""); } }}>
      <b>{t("iv.uusi_paate")}</b>
      <div className="rivi">
        <label>{t("iv.tunnus")} <input value={tunnus} onChange={(e) => setTunnus(e.target.value)} placeholder="P-5" /></label>
        <label>{t("pu.tyyppi")} <input value={tyyppi} onChange={(e) => setTyyppi(e.target.value)} /></label>
        <label>{t("iv.suunniteltu_ls")} <input inputMode="decimal" value={ls} onChange={(e) => setLs(e.target.value)} /></label>
        <label>{t("iv.toleranssi")} <input inputMode="decimal" value={tol} onChange={(e) => setTol(e.target.value)} /></label>
        <button className="nappi" type="submit" disabled={!tunnus.trim() || tyo}>{t("iv.lisaa_paate")}</button>
      </div>
    </form>
  );
}
